import { Component, OnInit, OnDestroy, TransferState, makeStateKey, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, takeUntil, Subject, take, finalize, tap, switchMap, of, EMPTY, catchError, forkJoin, map } from 'rxjs';
import { GameService } from '../../../../core/services/game.service';
import { Game, Tag } from '../../../../core/models/game.model';
import { ActivatedRoute } from '@angular/router';
import { FooterComponent } from '../../../../shared/components/footer/footer.component';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { ScrollToTopComponent } from '../../../../shared/components/scroll-to-top/scroll-to-top.component';
import { MatCardModule } from '@angular/material/card';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';
import { StartGameHelper } from '../../../../core/helpers/start-game.helper';
import { MatButtonModule } from '@angular/material/button';
import { Store } from '@ngrx/store';
import { AuthState, User } from '../../../../core/models/auth.model';
import { RouterLink } from '@angular/router';
import { roundTypeAlias, educationalLevel } from '../../../../core/aliases/game.aliases';
import { environment } from '../../../../../environments/environment';
import { TranslationService } from '../../../../core/services/translation.service';
import { SupportedLanguage } from '../../../../core/types/lang.type';
import { langPart } from '../../../../core/helpers/navigation.helper';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { MetaService } from '../../../../core/services/meta.service';
import { ScriptsService } from '../../../../core/services/script.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-quiz',
  templateUrl: './quiz.component.html',
  styleUrls: ['./quiz.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FooterComponent,
    HeaderComponent,
    ScrollToTopComponent,
    MatCardModule,
    MatButtonModule,
    RouterLink,
    TranslatePipe,
    MatSnackBarModule,
    MatProgressSpinner
]
})
export class QuizComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private store: Store<{ auth: AuthState }>,
    private gameService: GameService,
    private route: ActivatedRoute,
    private startGameHelper: StartGameHelper,
    private state: TransferState,
    private translationService: TranslationService,
    private snackBar: MatSnackBar,
    private metaService: MetaService,
    private scriptsService: ScriptsService,

  ) {
    this.currentUser$ = this.store.select(state => state.auth.user);
  }

  currentUser$: Observable<User | null>;
  slug: string = '';
  game: Game | null = null;
  roundAlias = roundTypeAlias;
  questionTypesText: string = '';
  stagesText: string = '';
  currentLang: SupportedLanguage = 'en';

  public cdn = environment.cdnUrl;
  langPart: string = '';
  gameLoading: boolean = false;
  pageLoading: boolean = true;
  notFound: boolean = false;

  ldData = {
    "@context": "https://schema.org",
    "@type": ["CreativeWork", "Quiz"],
    "name": "",
    "description": "",
    "about": {
      "@type": "Thing",
      "name": ""
    },
    "educationalLevel": "",
    "audience": {
      "@type": "Audience",
      "audienceType": ""
    },
    "image": "",
    "author": {
      "@type": "Person",
      "name": ""
    },
    "publisher": {
      "@type": "Organization",
      "name": "Xedoom",
      "url": "https://xedoom.com"
    },
    "isAccessibleForFree": true,
    "datePublished": "",
    "url": "",
    "learningResourceType": "Quiz",
    "educationalUse": "play",
    "typicalAgeRange": "12+",
    "genre": [
      ""
    ]
  };

  ngOnInit(): void {
    this.slug = this.route.snapshot.params['slug'];

    this.translationService.getCurrentLang().pipe(
      takeUntilDestroyed(this.destroyRef),

      switchMap(lang => {
        // turn ON for each attempt (handles lang/slug changes too)
        this.pageLoading = true;
        this.notFound = false;
        this.currentLang = lang;
        this.langPart = langPart(lang);

        const GAME_KEY = makeStateKey<Game>(`quiz-${this.slug}-${lang}`);
        const transferredGame = this.state.get<Game | null>(GAME_KEY, null);

        const load$ = transferredGame
          ? of(transferredGame)
          : this.gameService.getGameBySlug(this.slug, lang).pipe(
            tap(game => this.state.set(GAME_KEY, game)),
            catchError(err => {
              if (err?.status === 404) {
                this.notFound = true;
              } else {
                this.snackBar.open('Failed to load game', 'Close', { duration: 5000 });
              }
              return EMPTY; // no setupGame after errors
            })
          );

        return load$.pipe(
          finalize(() => { this.pageLoading = false; }) // ✅ always runs (success/error/unsubscribe)
        );
      })
    )
      .subscribe(game => this.setupGame(game));
  }

  private updateGameTexts(game: Game): void {
    forkJoin([
      this.translationService.getCurrentLang().pipe(
        take(1),
        map((lang: SupportedLanguage) => game.rounds.map(round => `${this.roundAlias[lang][round.roundContent.type]} (${round.count})`).join(', '))
      ),
      game.personalRounds && game.personalRounds > 0
        ? this.translationService.translate('quiz.including', game.personalRounds.toString()).pipe(take(1))
        : of(''),
      game.totalStages && game.totalStages > 0
        ? this.translationService.translate('quiz.in_rounds', game.totalStages.toString()).pipe(take(1))
        : of('')
    ]).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(([types, including, inRounds]: [string, string, string]) => {
      this.questionTypesText = types + including;
      this.stagesText = inRounds;
    });
  }

  private updateStructuredData(game: Game): void {
    const gameUrl = `https://xedoom.com/quiz/${game.slug}`;
    this.ldData.name = game.title.text;
    if (game.description) {
      this.ldData.description = game.description.text;
    }
    this.ldData.about.name = game.title.text;
    this.ldData.audience.audienceType = `${game.minTeamSize}-${game.maxTeamSize} players`;
    this.ldData.image = game.coverImage?.url || '';
    this.ldData.author.name = game.creator.name;
    this.ldData.educationalLevel = educationalLevel[game.difficulty as keyof typeof educationalLevel];
    this.ldData.isAccessibleForFree = game.isFree;
    this.ldData.datePublished = game.publishedAt;
    this.ldData.url = gameUrl;
    this.ldData.genre = game.gTags?.filter((tag: Tag) => tag.type === 'genre').map((tag: Tag) => tag.name) || [];

    this.metaService.updateStructuredData(this.ldData);
  }

  private setupGame(game: Game): void {
    this.game = game;
    if (this.game) {
      this.updateGameTexts(game);
      this.updateStructuredData(game);

      this.metaService.updateGameMeta({
        title: game.title.text,
        description: game.description?.text || '',
        url: `https://xedoom.com/quiz/${game.slug}`,
        imageUrl: game.coverImage?.url,
        keywords: 'Xedoom Quiz, team-based Quiz games, Quizzes, Riddles, team play, brain games, team-based Quizzes and Riddles, online games, team-based games'
      });

      if (game.coverImage?.url) {
        this.scriptsService.preloadCoverImage(game.coverImage.url);
      }
    }
  }

  ngOnDestroy(): void {
    this.metaService.cleanUp();
  }

  onStartGame(gameId: number | undefined): void {
    if (gameId) {
      if (this.gameLoading) return;
      this.gameLoading = true;
      this.startGameHelper.startGame(gameId).pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.gameLoading = false)
      ).subscribe();
    }
  }
}
